import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { modelAPI, apiKeyAPI, playgroundAPI, chatSessionAPI, knowledgeBaseAPI } from '../lib/api';
import { toast } from 'sonner';
import { Send, Trash2, Loader2, Copy, Settings, User, Bot, Save, History, Plus, MessageSquare, Database } from 'lucide-react';
import { cn } from '../lib/utils';

export default function PlaygroundPage() {
  const [searchParams] = useSearchParams();
  const [models, setModels] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);

  // Chat state
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedApiKey, setSelectedApiKey] = useState('');
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('You are a helpful assistant.');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [sending, setSending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [sessionName, setSessionName] = useState('');

  // RAG state
  const [useRag, setUseRag] = useState(false);
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [ragContext, setRagContext] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Set initial model from URL params
    const modelParam = searchParams.get('model');
    const vendorParam = searchParams.get('vendor');

    if (modelParam) {
      setSelectedModel(modelParam);

      // Find matching API key for vendor
      if (vendorParam && apiKeys.length > 0) {
        const matchingKey = apiKeys.find(k => k.vendor_code === vendorParam);
        if (matchingKey) {
          setSelectedApiKey(matchingKey.id);
        }
      }
    }
  }, [searchParams, apiKeys]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    try {
      const [modelsRes, keysRes, sessionsRes, kbsRes] = await Promise.all([
        modelAPI.list(),
        apiKeyAPI.list(),
        chatSessionAPI.list(),
        knowledgeBaseAPI.list(),
      ]);
      setModels(modelsRes.data);
      setApiKeys(keysRes.data);
      setSessions(sessionsRes.data);
      setKnowledgeBases(kbsRes.data);

      // Auto-select first key if available
      if (keysRes.data.length > 0 && !selectedApiKey) {
        setSelectedApiKey(keysRes.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load documents when knowledge bases are selected
  useEffect(() => {
    const loadDocuments = async () => {
      if (selectedKnowledgeBases.length === 0) {
        setAvailableDocuments([]);
        setSelectedDocuments([]);
        return;
      }

      setLoadingDocuments(true);
      try {
        const allDocs = [];
        for (const kbId of selectedKnowledgeBases) {
          const res = await knowledgeBaseAPI.listDocuments(kbId);
          allDocs.push(...res.data.map(doc => ({ ...doc, kb_id: kbId })));
        }
        setAvailableDocuments(allDocs);
        // Auto-select all documents
        setSelectedDocuments(allDocs.map(d => d.id));
      } catch (error) {
        toast.error('Failed to load documents');
      } finally {
        setLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, [selectedKnowledgeBases]);

  const loadSession = async (sessionId) => {
    try {
      const res = await chatSessionAPI.get(sessionId);
      setCurrentSessionId(sessionId);
      setSelectedModel(res.data.model_id);
      setSelectedApiKey(res.data.api_key_id);
      setSystemPrompt(res.data.system_prompt || 'You are a helpful assistant.');
      setMessages(res.data.messages || []);
      setShowHistory(false);
      toast.success('Session loaded');
    } catch (error) {
      toast.error('Failed to load session');
    }
  };

  const handleSend = async () => {
    if (!inputMessage.trim() || !selectedModel || !selectedApiKey) {
      toast.error('Please select a model, API key, and enter a message');
      return;
    }

    const userMessage = { role: 'user', content: inputMessage.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInputMessage('');
    setSending(true);

    try {
      let res;

      if (currentSessionId) {
        // Use session API
        res = await chatSessionAPI.sendMessage(currentSessionId, {
          api_key_id: selectedApiKey,
          model_id: selectedModel,
          messages: [{ role: 'user', content: inputMessage.trim() }],
          temperature,
          max_tokens: maxTokens,
        });
      } else {
        // Use direct playground API
        const apiMessages = [
          { role: 'system', content: systemPrompt },
          ...newMessages,
        ];

        res = await playgroundAPI.chat(
          selectedApiKey,
          selectedModel,
          apiMessages,
          temperature,
          maxTokens,
          useRag,
          selectedKnowledgeBases,
          selectedDocuments
        );
      }

      const assistantMessage = {
        role: 'assistant',
        content: res.data.message.content,
        usage: res.data.usage,
        response_time: res.data.response_time_ms,
      };

      // Store RAG context if available
      if (res.data.rag_context) {
        setRagContext(res.data.rag_context);
      }

      setMessages([...newMessages, assistantMessage]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get response');
      // Remove user message on error
      setMessages(messages);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setCurrentSessionId(null);
  };

  const saveSession = async () => {
    if (!sessionName.trim()) {
      toast.error('Please enter a session name');
      return;
    }

    try {
      const res = await chatSessionAPI.create({
        name: sessionName,
        model_id: selectedModel,
        api_key_id: selectedApiKey,
        system_prompt: systemPrompt,
      });

      setCurrentSessionId(res.data.id);
      setShowSaveDialog(false);
      setSessionName('');

      // Send existing messages to the new session
      if (messages.length > 0) {
        // We need to save messages separately - for now just create the session
        toast.success('Session created. Continue chatting to save messages.');
      } else {
        toast.success('Session created');
      }

      // Refresh sessions list
      const sessionsRes = await chatSessionAPI.list();
      setSessions(sessionsRes.data);
    } catch (error) {
      toast.error('Failed to create session');
    }
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Delete this session?')) return;

    try {
      await chatSessionAPI.delete(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
      }
      toast.success('Session deleted');
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Copied to clipboard');
  };

  const getSelectedKeyVendor = () => {
    const key = apiKeys.find(k => k.id === selectedApiKey);
    return key?.vendor_code;
  };

  const getAvailableModels = () => {
    const vendor = getSelectedKeyVendor();
    if (!vendor) return models;
    return models.filter(m => m.vendor_code === vendor);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-8rem)] flex gap-6" data-testid="playground-page">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight font-heading">Playground</h1>
              <p className="text-sm text-muted-foreground">
                {currentSessionId ? (
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" />
                    Session: {sessions.find(s => s.id === currentSessionId)?.name}
                  </span>
                ) : (
                  'Test your fine-tuned models'
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHistory(!showHistory)}
                data-testid="toggle-history-btn"
              >
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(!showSettings)}
                data-testid="toggle-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
              {messages.length > 0 && !currentSessionId && (
                <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="save-session-btn">
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Save Chat Session</DialogTitle>
                      <DialogDescription>
                        Save this conversation for later reference
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Session Name</Label>
                        <Input
                          data-testid="session-name-input"
                          placeholder="My conversation..."
                          value={sessionName}
                          onChange={(e) => setSessionName(e.target.value)}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
                      <Button onClick={saveSession} data-testid="submit-save-session-btn">Save</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
              <Button variant="outline" size="sm" onClick={clearChat} data-testid="clear-chat-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Clear
              </Button>
            </div>
          </div>

          {/* Model Selection */}
          <Card className="mb-4">
            <CardContent className="pt-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Select value={selectedApiKey} onValueChange={setSelectedApiKey}>
                    <SelectTrigger data-testid="playground-apikey-select">
                      <SelectValue placeholder="Select API key" />
                    </SelectTrigger>
                    <SelectContent>
                      {apiKeys.map((key) => (
                        <SelectItem key={key.id} value={key.id}>
                          {key.name} ({key.vendor_code})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Model</Label>
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger data-testid="playground-model-select">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableModels().map((model) => (
                        <SelectItem key={model.id} value={model.model_id}>
                          {model.name || model.model_id}
                        </SelectItem>
                      ))}
                      {/* Also allow entering custom model ID */}
                      {selectedModel && !getAvailableModels().find(m => m.model_id === selectedModel) && (
                        <SelectItem value={selectedModel}>{selectedModel}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Messages */}
          <Card className="flex-1 flex flex-col overflow-hidden">
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Start a conversation with your fine-tuned model</p>
                  </div>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "flex gap-3",
                      msg.role === 'user' ? "justify-end" : "justify-start"
                    )}
                    data-testid={`message-${idx}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-zinc-900 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "max-w-[80%] rounded-lg p-4",
                        msg.role === 'user'
                          ? "bg-zinc-900 text-white"
                          : "bg-zinc-100"
                      )}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                      {msg.usage && (
                        <div className="mt-2 pt-2 border-t border-zinc-200/50 flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{msg.usage.total_tokens} tokens</span>
                          <span>{msg.response_time}ms</span>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => copyMessage(msg.content)}
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </CardContent>

            {/* Input */}
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Textarea
                  ref={inputRef}
                  data-testid="chat-input"
                  placeholder="Type your message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={sending}
                  className="min-h-[44px] max-h-32 resize-none"
                  rows={1}
                />
                <Button
                  onClick={handleSend}
                  disabled={sending || !inputMessage.trim() || !selectedModel || !selectedApiKey}
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* History Panel */}
        {showHistory && (
          <Card className="w-80 flex-shrink-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Chat History</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => { clearChat(); setShowHistory(false); }}
                  data-testid="new-chat-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
              {sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No saved sessions yet
                </p>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.id}
                    className={cn(
                      "p-3 rounded-lg border cursor-pointer transition-colors hover:border-zinc-400",
                      currentSessionId === session.id && "border-zinc-900 bg-zinc-50"
                    )}
                    onClick={() => loadSession(session.id)}
                    data-testid={`session-item-${session.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{session.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {session.message_count} messages
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={(e) => { e.stopPropagation(); deleteSession(session.id); }}
                      >
                        <Trash2 className="w-3 h-3 text-destructive" />
                      </Button>
                    </div>
                    <Badge variant="outline" className="mt-2 text-xs capitalize">
                      {session.vendor_code}
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        )}

        {/* Settings Panel */}
        {showSettings && (
          <Card className="w-80 flex-shrink-0 flex flex-col overflow-hidden">
            <CardHeader>
              <CardTitle className="text-lg">Settings</CardTitle>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto space-y-6">
              <div className="space-y-2">
                <Label>System Prompt</Label>
                <Textarea
                  data-testid="system-prompt-input"
                  placeholder="You are a helpful assistant."
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <Label>Temperature</Label>
                  <span className="text-sm text-muted-foreground">{temperature}</span>
                </div>
                <Input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  data-testid="temperature-slider"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label>Max Tokens</Label>
                <Input
                  type="number"
                  min="1"
                  max="4096"
                  data-testid="max-tokens-input"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(parseInt(e.target.value) || 1024)}
                />
              </div>

              {/* RAG Settings */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Use Knowledge Base (RAG)</Label>
                    <p className="text-xs text-muted-foreground">
                      Enhance responses with your knowledge base
                    </p>
                  </div>
                  <Button
                    variant={useRag ? "default" : "outline"}
                    size="sm"
                    onClick={() => setUseRag(!useRag)}
                  >
                    <Database className="w-4 h-4 mr-2" />
                    {useRag ? 'On' : 'Off'}
                  </Button>
                </div>

                {useRag && (
                  <>
                    <div className="space-y-2">
                      <Label>Select Knowledge Bases</Label>
                      <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                        {knowledgeBases.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-2">
                            No knowledge bases available
                          </p>
                        ) : (
                          knowledgeBases.map((kb) => (
                            <label
                              key={kb.id}
                              className="flex items-center gap-2 p-2 rounded hover:bg-zinc-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedKnowledgeBases.includes(kb.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedKnowledgeBases([...selectedKnowledgeBases, kb.id]);
                                  } else {
                                    setSelectedKnowledgeBases(selectedKnowledgeBases.filter(id => id !== kb.id));
                                  }
                                }}
                                className="rounded"
                              />
                              <span className="text-sm flex-1">{kb.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {kb.document_count} docs
                              </Badge>
                            </label>
                          ))
                        )}
                      </div>
                    </div>

                    {selectedKnowledgeBases.length > 0 && (
                      <div className="space-y-2">
                        <Label>Select Documents</Label>
                        {loadingDocuments ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-4 h-4 animate-spin" />
                          </div>
                        ) : (
                          <div className="border rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                            {availableDocuments.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-2">
                                No documents available
                              </p>
                            ) : (
                              availableDocuments.map((doc) => (
                                <label
                                  key={doc.id}
                                  className="flex items-center gap-2 p-2 rounded hover:bg-zinc-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedDocuments.includes(doc.id)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedDocuments([...selectedDocuments, doc.id]);
                                      } else {
                                        setSelectedDocuments(selectedDocuments.filter(id => id !== doc.id));
                                      }
                                    }}
                                    className="rounded"
                                  />
                                  <span className="text-sm flex-1">{doc.name}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {doc.chunk_count} chunks
                                  </Badge>
                                </label>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {selectedModel && (
                <div className="pt-4 border-t">
                  <Label className="text-muted-foreground">Current Model</Label>
                  <p className="text-sm font-mono mt-1 break-all">{selectedModel}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
